import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/auth';
import { useWardrobeItems, WardrobeItem } from '../hooks/useWardrobeItems';
import { Layout } from '../components/Layout';
import { MessageSquare, Plus, Upload } from 'lucide-react';
import UserUploadsGallery from '../components/UserUploadsGallery';
import { PaperSection } from '../components/PaperSection';
import ImageUploader from '../components/ImageUploader';
import { useUserUploads } from '../hooks/useUserUploads';
import FeedbackWidget from '../components/FeedbackWidget';

export default function Home() {
  const { profile } = useAuthContext();
  const navigate = useNavigate();
  const { items, loading, error, getItems } = useWardrobeItems(profile?.id);
  const [refreshing, setRefreshing] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const { uploads: userUploads, loading: uploadsLoading, refreshUploads } = useUserUploads();

  // Fetch wardrobe items on component mount
  useEffect(() => {
    getItems();
  }, [profile?.id]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    // Refresh both wardrobe items and user uploads
    await Promise.all([getItems(), refreshUploads()]);
    setRefreshing(false);
  };

  // Navigate to add screen
  const handleAddItem = () => {
    navigate('/add');
  };

  // Handle showing the uploader modal
  const toggleUploader = () => {
    setShowUploader(!showUploader);
  };

  // Handle successful upload
  const handleUploadSuccess = () => {
    setShowUploader(false);
    // Force a refresh of the user uploads
    refreshUploads().then(() => {
      setRefreshing(false);
    });
  };

  // Extract the user's first name from the profile
  const firstName = profile?.name?.split(' ')[0] || 'there';

  // Check if we're in empty state
  const isEmptyState = userUploads.length === 0 && !uploadsLoading;

  // Create the modal separately
  const uploadModal = showUploader && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-haus-light-black bg-opacity-50" onClick={() => setShowUploader(false)}>
      <div className="bg-haus-light-white rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-medium mb-4">Upload a Photo</h3>
        <ImageUploader onUploadSuccess={handleUploadSuccess} />
        <div className="flex justify-end mt-4">
          <button 
            onClick={() => setShowUploader(false)}
            className="px-3 py-1.5 border border-haus-dark-green bg-transparent text-haus-dark-green text-sm rounded hover:bg-haus-light-olive hover:text-haus-light-white hover:border-haus-light-olive transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Layout>
        <div className="space-y-6 sm:space-y-8 px-4 sm:px-0">
          {/* Page Header with Upload Button - only show button if not in empty state */}
          <div className="flex justify-between items-center">
            <h1 className="text-2xl sm:text-3xl font-display page-title">My Photos</h1>
            {!isEmptyState && (
              <button
                onClick={toggleUploader}
                className="flex items-center gap-2 px-3 py-2 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors"
              >
                <Upload size={16} />
                <span className="text-sm font-medium">Upload</span>
              </button>
            )}
          </div>

          {/* Empty State */}
          {isEmptyState && (
            <div className="py-16 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-2">No Photos Yet</h3>
              <p className="text-gray-500 text-sm max-w-md mb-6">
                Upload photos of yourself to try on different outfits using our virtual try-on feature.
              </p>
              <button
                onClick={toggleUploader}
                className="flex items-center gap-2 px-4 py-2 bg-haus-dark-green text-haus-light-white rounded hover:bg-haus-light-olive transition-colors"
              >
                <Upload size={16} />
                <span className="text-sm font-medium">Upload Your First Photo</span>
              </button>
            </div>
          )}

          {/* User Uploads Gallery - now using bento grid */}
          {(userUploads.length > 0 || uploadsLoading) && (
            <div className="py-2">
              <UserUploadsGallery onRefresh={handleRefresh} />
            </div>
          )}
        </div>
        <FeedbackWidget />
      </Layout>
      
      {/* Upload Modal - Placed outside Layout component */}
      {uploadModal}
    </>
  );
}

function WardrobeItemCard({ item }: { item: WardrobeItem }) {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden">
      <div className="aspect-square">
        <img 
          src={item.image_url} 
          alt={item.name} 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-2 sm:p-3">
        <h3 className="font-medium text-sm sm:text-base">{item.name}</h3>
        <div className="flex flex-wrap gap-1 sm:gap-2 mt-1 sm:mt-2">
          <span className="bg-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs text-gray-600">
            {item.type}
          </span>
          {item.style && (
            <span className="bg-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs text-gray-600">
              {item.style}
            </span>
          )}
        </div>
      </div>
    </div>
  );
} 